import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import './LopGridContextMenu.css';

export interface LopMenuItem {
  label: string;
  /** Leaf action. Optional only when the item has `children`. */
  onClick?: () => void;
  disabled?: boolean;
  /** Optional leading icon (lucide-react component or any ReactNode). 16px is
   *  the conventional size — see LopCrudPage callers. */
  icon?: React.ReactNode;
  /** Render a separator line above this item (visual grouping). */
  dividerBefore?: boolean;
  /** Submenu. The item then shows a chevron and opens the submenu on hover /
   *  click / ArrowLeft / Enter (RTL: the submenu opens to the LEFT). */
  children?: LopMenuItem[];
  /** Marks the currently-active option (renders a check mark). `false` keeps
   *  the check slot empty so sibling labels stay aligned. */
  checked?: boolean;
  /** Right-aligned muted preview text (e.g. a number-format string). */
  trailing?: React.ReactNode;
}

interface LopGridContextMenuProps {
  x: number; // viewport coords (clientX)
  y: number; // viewport coords (clientY)
  items: LopMenuItem[];
  onClose: () => void;
}

// Generic, reusable positioned menu. No domain knowledge — the caller supplies
// the items. Renders a fixed-position role="menu" at (x, y), clamped to the
// viewport, and closes on outside mousedown / Esc / scroll / window blur.
//
// Submenus are rendered as DOM descendants of their parent <li>, so the single
// outside-click check (`menuRef.contains`) automatically treats clicks inside
// any submenu as inside. Each submenu is `position: fixed` and clamped to the
// viewport after layout (flips to the right of the parent when there is no
// room on the left; shifts up when it would overflow the bottom).
export function LopGridContextMenu({ x, y, items, onClose }: LopGridContextMenuProps) {
  const menuRef = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // Clamp after layout so we know the rendered size. If it overflows the
  // viewport on either axis, shift it back so it stays fully visible.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + width > window.innerWidth) left = Math.max(0, window.innerWidth - width);
    if (top + height > window.innerHeight) top = Math.max(0, window.innerHeight - height);
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    // Capture-phase scroll: catches scrolling inside the grid body too.
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  return (
    <ul
      ref={menuRef}
      role="menu"
      className="lop-grid-context-menu"
      style={{ left: pos.left, top: pos.top }}
    >
      <MenuItems items={items} onClose={onClose} />
    </ul>
  );
}

interface MenuItemsProps {
  items: LopMenuItem[];
  onClose: () => void;
}

// Renders one level of items; owns which child submenu (if any) is open.
function MenuItems({ items, onClose }: MenuItemsProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);

  return (
    <>
      {items.map((item, index) => {
        const dividerNode = item.dividerBefore ? (
          <li
            key={`div-${index}`}
            role="separator"
            className="lop-grid-context-menu__divider"
          />
        ) : null;

        if (item.disabled) {
          return (
            <React.Fragment key={`${index}-${item.label}`}>
              {dividerNode}
              <li
                role="menuitem"
                aria-disabled="true"
                className="lop-grid-context-menu__item lop-grid-context-menu__item--disabled"
                onMouseEnter={() => setOpenIndex(null)}
              >
                {item.icon && (
                  <span className="lop-grid-context-menu__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="lop-grid-context-menu__label">{item.label}</span>
              </li>
            </React.Fragment>
          );
        }

        const hasChildren = Boolean(item.children && item.children.length > 0);
        const isOpen = hasChildren && openIndex === index;

        return (
          <React.Fragment key={`${index}-${item.label}`}>
            {dividerNode}
            <li
              role="none"
              className="lop-grid-context-menu__row"
              onMouseEnter={() => {
                setOpenedByKeyboard(false);
                setOpenIndex(hasChildren ? index : null);
              }}
            >
              <button
                type="button"
                role="menuitem"
                aria-haspopup={hasChildren ? 'menu' : undefined}
                aria-expanded={hasChildren ? isOpen : undefined}
                className="lop-grid-context-menu__item"
                onClick={() => {
                  if (hasChildren) {
                    setOpenedByKeyboard(false);
                    setOpenIndex(index);
                    return;
                  }
                  item.onClick?.();
                  onClose();
                }}
                onKeyDown={(e) => {
                  if (hasChildren && (e.key === 'ArrowLeft' || e.key === 'Enter')) {
                    e.preventDefault();
                    setOpenedByKeyboard(true);
                    setOpenIndex(index);
                  }
                }}
              >
                {item.icon && (
                  <span className="lop-grid-context-menu__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="lop-grid-context-menu__label">{item.label}</span>
                {item.trailing != null && (
                  <span className="lop-grid-context-menu__trailing">{item.trailing}</span>
                )}
                {item.checked !== undefined && (
                  <span className="lop-grid-context-menu__check" aria-hidden="true">
                    {item.checked && <Check size={14} />}
                  </span>
                )}
                {hasChildren && (
                  <span className="lop-grid-context-menu__chevron" aria-hidden="true">
                    <ChevronLeft size={14} />
                  </span>
                )}
              </button>
              {isOpen && (
                <Submenu
                  items={item.children!}
                  onClose={onClose}
                  autoFocus={openedByKeyboard}
                  onBack={() => setOpenIndex(null)}
                />
              )}
            </li>
          </React.Fragment>
        );
      })}
    </>
  );
}

interface SubmenuProps {
  items: LopMenuItem[];
  onClose: () => void;
  autoFocus: boolean;
  onBack: () => void;
}

const SUBMENU_GAP = 2;
const VIEWPORT_MARGIN = 8;

function Submenu({ items, onClose, autoFocus, onBack }: SubmenuProps) {
  const ref = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Position relative to the parent row (the <li> that contains us). Preferred
  // side is LEFT (RTL); flip to the right if it would leave the viewport.
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const { width, height } = el.getBoundingClientRect();
    const p = parent.getBoundingClientRect();
    let left = p.left - width - SUBMENU_GAP;
    if (left < VIEWPORT_MARGIN) {
      left = p.right + SUBMENU_GAP;
      if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
      }
    }
    let top = p.top - 4;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = window.innerHeight - height - VIEWPORT_MARGIN;
    }
    top = Math.max(VIEWPORT_MARGIN, top);
    setPos({ left, top });
  }, []);

  useEffect(() => {
    if (!autoFocus || !pos) return;
    ref.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]')?.focus();
  }, [autoFocus, pos]);

  return (
    <ul
      ref={ref}
      role="menu"
      className="lop-grid-context-menu lop-grid-context-menu--sub"
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          e.stopPropagation();
          onBack();
          (ref.current?.parentElement?.querySelector(
            ':scope > button',
          ) as HTMLButtonElement | null)?.focus();
        }
      }}
    >
      <MenuItems items={items} onClose={onClose} />
    </ul>
  );
}
